import { createContext, FC, PropsWithChildren, useCallback, useContext, useState } from "react";

export interface ShopCartLineItem {
  parentRateId: string;
  childRateId: string | null;
  quantity: number;
}

interface ShopCartContextValue {
  lineItems: ShopCartLineItem[];
  addToCart: (parentRateId: string, childRateId: string | null, quantity: number) => void;
  decrementItem: (itemKey: string) => void;
  removeAllForRate: (parentRateId: string) => void;
}

const ShopCartContext = createContext<ShopCartContextValue | null>(null);

export const useShopCartContext = (): ShopCartContextValue | null => {
  return useContext(ShopCartContext);
};

export const ShopCartProvider: FC<PropsWithChildren> = ({ children }) => {
  const [lineItems, setLineItems] = useState<ShopCartLineItem[]>([]);

  const addToCart = useCallback((parentRateId: string, childRateId: string | null, quantity: number) => {
    setLineItems((prev) => {
      const itemKey = childRateId ?? parentRateId;
      const existingIndex = prev.findIndex((item) => (item.childRateId ?? item.parentRateId) === itemKey);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }
      return [...prev, { parentRateId, childRateId, quantity }];
    });
  }, []);

  const decrementItem = useCallback((itemKey: string) => {
    setLineItems((prev) => {
      const index = prev.findIndex((item) => (item.childRateId ?? item.parentRateId) === itemKey);
      if (index < 0) {
        return prev;
      }
      if (prev[index].quantity <= 1) {
        return prev.filter((_, i) => i !== index);
      }
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: updated[index].quantity - 1 };
      return updated;
    });
  }, []);

  const removeAllForRate = useCallback((parentRateId: string) => {
    setLineItems((prev) => prev.filter((item) => item.parentRateId !== parentRateId));
  }, []);

  return (
    <ShopCartContext.Provider value={{ lineItems, addToCart, decrementItem, removeAllForRate }}>
      {children}
    </ShopCartContext.Provider>
  );
};
